import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import type { Extension } from '@codemirror/state'
import { EditorView } from '@codemirror/view'
import { tags as t } from '@lezer/highlight'

export const SAUCES = [
  { id: 'squid-ink', label: 'squid ink', hint: 'dark · indigo', dark: true },
  { id: 'pesto', label: 'pesto', hint: 'dark · green', dark: true },
  { id: 'marinara', label: 'marinara', hint: 'dark · red', dark: true },
  { id: 'vodka', label: 'vodka', hint: 'dark · blush', dark: true },
  { id: 'vongole', label: 'vongole', hint: 'dark · teal', dark: true },
  { id: 'cacio-e-pepe', label: 'cacio e pepe', hint: 'dark · slate', dark: true },
  { id: 'alfredo', label: 'alfredo', hint: 'light · cream', dark: false },
  { id: 'primavera', label: 'primavera', hint: 'light · sage', dark: false },
] as const

export type SauceId = (typeof SAUCES)[number]['id']

export const DEFAULT_SAUCE: SauceId = 'squid-ink'
const STORAGE_KEY = 'pastabin:sauce'

function isSauceId(value: unknown): value is SauceId {
  return SAUCES.some((sauce) => sauce.id === value)
}

export function currentSauce(): SauceId {
  const attribute = document.documentElement.dataset.sauce
  if (isSauceId(attribute)) return attribute
  return DEFAULT_SAUCE
}

/** Palettes declare their own polarity, so adding a light sauce needs no edits here. */
export function isDarkSauce(id: SauceId): boolean {
  return SAUCES.find((sauce) => sauce.id === id)?.dark ?? true
}

export function applySauce(id: SauceId): void {
  document.documentElement.dataset.sauce = id
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Private browsing — the sauce just won't stick.
  }
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', token('--surface'))
}

/** Reads one palette custom property off the root, so callers never hardcode a colour. */
export function paletteToken(name: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || '#808080'
}

/** Local shorthand — editorTheme() below reads two dozen of these in a row. */
const token = paletteToken

/**
 * Builds the editor theme from the CSS custom properties of the active sauce,
 * so the palettes only ever live in one place (styles.css).
 */
export function editorTheme(): Extension {
  const bg = token('--bg')
  const fg = token('--fg')
  const muted = token('--muted')
  const border = token('--border')
  const surface = token('--surface')
  const surfaceAlt = token('--surface-alt')
  const accent = token('--accent')
  const accentFg = token('--accent-fg')
  const selection = token('--sel')
  const cursor = token('--cursor')
  const activeLine = token('--active-line')
  const gutterFg = token('--gutter-fg')

  const theme = EditorView.theme(
    {
      '&': { color: fg, backgroundColor: bg, height: '100%' },
      '.cm-scroller': { fontFamily: 'inherit', lineHeight: '1.6' },
      '.cm-content': { caretColor: cursor, padding: '12px 0' },
      '.cm-cursor, .cm-dropCursor': { borderLeftColor: cursor, borderLeftWidth: '2px' },
      '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
        backgroundColor: selection,
      },
      '.cm-activeLine': { backgroundColor: activeLine },
      '.cm-gutters': { backgroundColor: bg, color: gutterFg, border: 'none' },
      '.cm-activeLineGutter': { backgroundColor: activeLine, color: muted },
      '.cm-lineNumbers .cm-gutterElement': { padding: '0 10px 0 16px', minWidth: '46px' },
      '.cm-placeholder': { color: muted },
      '.cm-selectionMatch': { backgroundColor: 'transparent', outline: `1px solid ${border}` },
      '.cm-searchMatch': { backgroundColor: selection, outline: `1px solid ${border}` },
      '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: accent, color: accentFg },
      '.cm-panels': { backgroundColor: surface, color: fg },
      '.cm-panels.cm-panels-bottom': { borderTop: `1px solid ${border}` },
      '.cm-panel input, .cm-panel button, .cm-panel select': {
        backgroundColor: surfaceAlt,
        color: fg,
        border: `1px solid ${border}`,
        borderRadius: '5px',
        font: 'inherit',
        fontSize: '12px',
      },
      '.cm-tooltip': { backgroundColor: surfaceAlt, color: fg, border: `1px solid ${border}` },
      '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
        backgroundColor: 'transparent',
        color: accent,
        outline: `1px solid ${border}`,
      },
    },
    { dark: isDarkSauce(currentSauce()) },
  )

  const highlight = HighlightStyle.define([
    { tag: [t.keyword, t.modifier, t.controlKeyword, t.operatorKeyword, t.definitionKeyword], color: token('--syn-keyword') },
    { tag: [t.string, t.special(t.string), t.regexp], color: token('--syn-string') },
    { tag: [t.number, t.bool, t.null, t.atom], color: token('--syn-number') },
    { tag: [t.comment, t.lineComment, t.blockComment, t.docComment], color: token('--syn-comment'), fontStyle: 'italic' },
    { tag: [t.variableName, t.propertyName, t.attributeName], color: token('--syn-name') },
    { tag: [t.typeName, t.className, t.namespace, t.tagName], color: token('--syn-type') },
    { tag: [t.function(t.variableName), t.function(t.propertyName)], color: token('--syn-type') },
    { tag: [t.operator, t.punctuation, t.separator, t.bracket], color: token('--syn-operator') },
    { tag: [t.meta, t.annotation, t.processingInstruction], color: token('--syn-meta') },
    { tag: [t.heading, t.strong], color: token('--syn-heading'), fontWeight: '600' },
    { tag: t.emphasis, fontStyle: 'italic' },
    { tag: t.strikethrough, textDecoration: 'line-through' },
    { tag: t.link, color: token('--syn-name'), textDecoration: 'underline' },
    { tag: t.invalid, color: token('--syn-invalid') },
  ])

  return [theme, syntaxHighlighting(highlight)]
}
